import type { Service } from '@src/gen/ai/h2o/cloud/discovery/v1/service_pb';
import { CloudPlatformDiscoveryConfig } from '@shared/contexts';
import { getUrlFromUri } from '@shared/lib/utils';
import { LocalStorageKeys } from '@shared/lib/utils/localStorage';
import { clientNames, serviceNames } from '@shared/services/discovery/constants';
import { getClient, getEnvironment, listServices } from '@shared/services/discovery/discoveryService.functions';

interface BootstrapResult {
  discovery: CloudPlatformDiscoveryConfig;
  auth: {
    authority: string;
    clientId: string;
  };
}

/**
 * Derives service URLs from the discovery service response
 */
const deriveServiceUrls = (services: Service[]) => {
  const deriveUrl = (serviceName: string) => {
    const service = services.find((s) => s.name === serviceName);
    return getUrlFromUri(service?.uri);
  };

  return {
    aiEngineManagerApiUrl: deriveUrl(serviceNames.aiem),
    // Featurestore URL needs special handling - remove trailing /api because the generated SDK adds it
    featurestoreApiUrl: deriveUrl(serviceNames.featurestore)?.replace(/\/api$/, ''),
    telemetryApiUrl: deriveUrl(serviceNames.telemetry),
    mlopsApiUrl: deriveUrl(serviceNames.mlops),
    orchestratorApiUrl: deriveUrl(serviceNames.orchestrator),
    notebookApiUrl: deriveUrl(serviceNames.notebookAPI),
    secureStore: deriveUrl(serviceNames.secureStore),
    authzUrl: deriveUrl(serviceNames.authz),
    mcAdminCenterApiUrl: deriveUrl(serviceNames.mcAdminCenter),
    auditTrailApiUrl: deriveUrl(serviceNames.auditTrail),
    stsDriveUrl: deriveUrl(serviceNames.stsDrive),
    userDriveUrl: deriveUrl(serviceNames.userDrive),
    workspaceDriveUrl: deriveUrl(serviceNames.workspaceDrive),
    sessionServer: deriveUrl(serviceNames.sessionServer),
    workflowApiUrl: deriveUrl(serviceNames.workflows),
    h2oGpte: deriveUrl(serviceNames.h2oGpte),
    hacAssistantApiUrl: deriveUrl(serviceNames.hacAssistant),
  };
};

/**
 * Sets up localStorage with OIDC key and optionally mock session for development
 */
const setupLocalStorage = async (providerUrl: string, platformClientId: string) => {
  const oidcKey = `oidc.user:${providerUrl}:${platformClientId}`;
  window.localStorage.setItem(LocalStorageKeys.OIDC_KEY, oidcKey);

  // Set fake session for using mock server for local development
  if (
    import.meta.env.MODE === 'development' &&
    import.meta.env.VITE_USE_MOCK_SERVER === `1` &&
    !import.meta.env.VITE_USE_IDP
  ) {
    const { OidcSession } = await import('@src/test/mocks/OidcSession');
    window.localStorage.setItem(oidcKey, JSON.stringify(OidcSession));
  }
};

/**
 * Fetches discovery information and bootstraps the application
 * @throws Error if required configuration is missing
 */
export const bootstrapDiscovery = async (): Promise<BootstrapResult> => {
  const [environmentResponse, clientResponse, listServicesResponse] = await Promise.all([
    getEnvironment({}),
    getClient({ name: clientNames.platform }),
    listServices({ pageSize: 1000 }),
  ]);

  const { environment } = environmentResponse;
  const services = listServicesResponse.services || [];
  const serviceUrls = deriveServiceUrls(services);

  const providerUrl = environment?.issuerUrl;
  const platformClientId = clientResponse.client?.oauth2ClientId;
  const h2oCloudEnvironment = environment?.h2oCloudEnvironment;

  // Validate required configuration
  if (!providerUrl || !platformClientId) {
    const errors: string[] = [];
    if (!platformClientId) errors.push('No platform client ID available from the identity provider.');
    if (!providerUrl) errors.push('No platform identity provider location available.');
    throw new Error(errors.join(' '));
  }

  // Setup localStorage
  await setupLocalStorage(providerUrl, platformClientId);

  return {
    discovery: {
      ...serviceUrls,
      providerUrl,
      platformClientId,
      h2oCloudEnvironment,
    },
    auth: {
      authority: providerUrl,
      clientId: platformClientId,
    },
  };
};
